import { useEffect, useMemo, useRef, useState } from 'react'
import { FormikProps } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { RegisterTemplateProps } from '../../../../templates/Register'
import { useDefaultLayoutProps } from '../../../../layouts/DefaultLayout/useProps'
import { RegisterFormValues } from '../../../../components/forms/RegisterForm'
import { actions, selectors } from '../../../../redux'
import { groupSlotsByDay } from '../../../../helpers/SlotsHelpers'
import { formatHour, isSameDay } from '../../../../helpers/DateHelpers'
import { useRegisterForm } from '../../../../templates/Register/useRegisterFormSchema'

export const useUpdateSlotProps = (): RegisterTemplateProps => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const locale = useSelector(selectors.app.currentLocale)
  const registerCode = useSelector(selectors.app.registerCode)

  const user = useSelector(selectors.app.visitor)
  const update = useSelector(selectors.app.updateRegistration)

  const layoutProps = useDefaultLayoutProps()

  const [selectedDate, setSelectedDate] = useState<Date>()
  const [selectedSlot, setSelectedSlot] = useState<string>()
  const [formErrors, setFormErrors] = useState<string[]>()

  const $formikRef = useRef<FormikProps<RegisterFormValues>>(null)

  const availableDates = useMemo(() => groupSlotsByDay(user?.event?.slots ?? []), [
    user?.event?.slots,
  ])

  const slots = useMemo(
    () =>
      availableDates.find((day) => {
        const date = new Date(day.date)
        return isSameDay(date, selectedDate)
      })?.slots,
    [availableDates, selectedDate]
  )

  const hasAttendant = false

  const { handleValidate } = useRegisterForm({
    hasAttendant,
    formRef: $formikRef,
    onValidationError: (errors) => setFormErrors(errors),
  })

  // WHEN SELECTED DATE CHANGE, RESET SELECTED SLOT
  useEffect(() => {
    if (selectedDate) {
      setSelectedSlot(undefined)
    }
  }, [selectedDate])

  const opt = user?.optinRegistration ?? false

  return useMemo(
    () => ({
      layout: layoutProps,
      titleDescription: {
        description: user?.event?.description,
        title: user?.event?.frontTitle ?? '',
        imageDescription: user?.event?.frontDescription ?? '',
        image: { src: user?.event?.frontImage ?? '', alt: 'cover' },
        isCentered: true,
      },
      registerForm: {
        formik: {
          innerRef: $formikRef,
          initialValues: {
            firstName: user?.first_name || '',
            lastName: user?.last_name || '',
            email: user?.email || '',
            terms: true,
            opt,
          },
          validate: handleValidate,
          onSubmit: (data) => {
            dispatch(
              actions.app.updateRegistrationRequest({
                eventSlot: selectedSlot!,
                optinRegistration: data?.opt ?? false,
              })
            )
          },
        },
        fields: {
          firstName: {
            name: 'firstName',
            label: t('register.form.firstName.label'),
            disabled: true,
          },
          lastName: {
            name: 'lastName',
            label: t('register.form.lastName.label'),
            disabled: true,
          },
          email: {
            name: 'email',
            label: t('register.form.email.label'),
            disabled: true,
          },
          terms: {
            name: 'terms',
            label: t('register.form.terms.label'),
            disabled: true,
          },
          opt: !opt
            ? {
                name: 'opt',
                label: t('register.form.opt.label'),
              }
            : undefined,
        },
      },
      calendar: {
        title: t('register.calendar.title'),
        legend: t('register.calendar.legend'),
        cells: availableDates.map((day) => ({ date: new Date(day.date), type: 'available' })),
        selectedDate,
        onSelect: (date) => setSelectedDate(date),
      },
      ...(selectedDate &&
        slots && {
          slots: {
            title: t('register.slots.title'),
            items: slots.map((slot) => ({
              id: slot.id,
              disabled: hasAttendant ? slot.remaining_capacity < 2 : slot.remaining_capacity === 0,
              selected: selectedSlot === slot.id,
              label: `${formatHour(slot.opening_hour, locale)} - ${formatHour(
                slot.closing_hour,
                locale
              )}`,
            })),
            onChange: (item) => {
              setSelectedSlot(item.id)
            },
          },
        }),
      errors: registerCode ? update?.errors : formErrors?.map((error) => ({ label: error })),
      submitButton: {
        text: t('register.form.submitButton'),
        disabled: !selectedSlot || update?.pending,
        onClick: () => $formikRef.current?.submitForm(),
      },
    }),
    [
      user,
      selectedDate,
      layoutProps,
      dispatch,
      hasAttendant,
      availableDates,
      selectedSlot,
      registerCode,
      handleValidate,
      t,
      slots,
      formErrors,
      update,
      locale,
      opt,
    ]
  )
}
